import OpenAI from 'openai';
import { storageService } from './storage';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true // Note: In production, you should use a backend API
});

export interface StoryGenerationRequest {
  prompt: string;
  genre: string;
  ageGroup: string;
  storyLength: string;
  illustrationStyle: string;
  mainCharacter?: string;
  characterDetails?: string;
}

export interface StoryPage {
  pageNumber: number;
  text: string;
  imagePrompt: string;
  illustrationStyle: string;
  imageUrl?: string;
}

export interface GeneratedStory {
  title: string;
  pages: StoryPage[];
  metadata: {
    totalPages: number;
    estimatedReadTime: number;
    generatedAt: string;
    genre: string;
    ageGroup: string;
  };
}

export const aiService = {
  // Generate a complete story with multiple pages
  async generateStory(request: StoryGenerationRequest): Promise<GeneratedStory> {
    try {
      // Determine number of pages based on story length
      const pageCount = this.getPageCount(request.storyLength);
      
      // Generate story outline first
      const outline = await this.generateStoryOutline(request, pageCount);
      
      // Generate each page
      const pages: StoryPage[] = [];
      for (let i = 1; i <= pageCount; i++) {
        const page = await this.generateStoryPage(request, outline, i, pageCount);
        
        // Generate image for this page
        const imageUrl = await this.generateImage(page.imagePrompt, page.illustrationStyle);
        page.imageUrl = imageUrl;
        
        pages.push(page);
      }
      
      // Generate title
      const title = await this.generateStoryTitle(request, outline);
      
      return {
        title,
        pages,
        metadata: {
          totalPages: pageCount,
          estimatedReadTime: this.calculateReadTime(pages),
          generatedAt: new Date().toISOString(),
          genre: request.genre,
          ageGroup: request.ageGroup
        }
      };
    } catch (error) {
      console.error('Error generating story:', error);
      throw new Error('Failed to generate story');
    }
  },

  // Generate story outline
  async generateStoryOutline(request: StoryGenerationRequest, pageCount: number): Promise<string> {
    const prompt = `Create a ${request.storyLength} story outline for a ${request.ageGroup} year old child about: "${request.prompt}"

Genre: ${request.genre}
Main character: ${request.mainCharacter || 'A child'}
Character details: ${request.characterDetails || 'Brave and curious'}

The story should be ${pageCount} pages long. For each page, provide:
1. A brief summary of what happens
2. The key elements that should be illustrated
3. The emotional tone

Make it engaging, age-appropriate, and educational. Include a clear beginning, middle, and end.`;

    const response = await openai.chat.completions.create({
      model: 'gpt-4o',
      messages: [
        {
          role: 'system',
          content: 'You are a children\'s story writer who creates engaging, age-appropriate stories with clear educational value and positive messages.'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      max_tokens: 1000,
      temperature: 0.8
    });

    return response.choices[0]?.message?.content || '';
  },

  // Generate a single story page
  async generateStoryPage(
    request: StoryGenerationRequest, 
    outline: string, 
    pageNumber: number, 
    totalPages: number
  ): Promise<StoryPage> {
    const prompt = `Based on this story outline:
${outline}

Generate page ${pageNumber} of ${totalPages} for a ${request.ageGroup} year old child.

Requirements:
- Age-appropriate language for ${request.ageGroup}
- Engaging and educational content
- Clear narrative progression
- 2-3 sentences maximum for this age group
- Include dialogue and action

Also provide an image prompt for ${request.illustrationStyle} style illustration that matches the page content.`;

    const response = await openai.chat.completions.create({
      model: 'gpt-4o',
      messages: [
        {
          role: 'system',
          content: 'You are a children\'s book writer and illustrator. Provide both the story text and an image prompt in this format:\n\nTEXT: [story text]\nIMAGE: [detailed image prompt]'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      max_tokens: 500,
      temperature: 0.7
    });

    const content = response.choices[0]?.message?.content || '';
    
    // Parse the response to extract text and image prompt
    const textMatch = content.match(/TEXT:\s*(.+?)(?=\nIMAGE:|$)/s);
    const imageMatch = content.match(/IMAGE:\s*(.+?)$/s);
    
    return {
      pageNumber,
      text: textMatch?.[1]?.trim() || 'Once upon a time...',
      imagePrompt: imageMatch?.[1]?.trim() || 'A magical scene',
      illustrationStyle: request.illustrationStyle
    };
  },

  // Generate story title
  async generateStoryTitle(request: StoryGenerationRequest, outline: string): Promise<string> {
    const prompt = `Based on this story outline:
${outline}

Generate a catchy, age-appropriate title for a ${request.ageGroup} year old child. The title should be:
- Short and memorable
- Reflect the main theme
- Be engaging for children
- Include the main character if relevant

Just provide the title, nothing else.`;

    const response = await openai.chat.completions.create({
      model: 'gpt-4o',
      messages: [
        {
          role: 'system',
          content: 'You are a children\'s book editor who creates engaging titles.'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      max_tokens: 50,
      temperature: 0.8
    });

    return response.choices[0]?.message?.content?.trim() || 'The Magic Adventure';
  },

  // Generate image for a story page using DALL-E 3 and upload to Supabase Storage
  async generateImage(imagePrompt: string, style: string): Promise<string> {
    try {
      // Enhance the prompt based on the illustration style
      const enhancedPrompt = this.enhanceImagePrompt(imagePrompt, style);
      
      const response = await openai.images.generate({
        model: "dall-e-3",
        prompt: enhancedPrompt,
        n: 1,
        size: "1024x1024",
        quality: "standard",
        style: "vivid"
      });

      const dalleImageUrl = response.data[0]?.url;
      if (!dalleImageUrl) {
        throw new Error('No image URL returned from DALL-E');
      }

      // For now, return the DALL-E URL directly
      // The actual upload will happen in the story creation process
      return dalleImageUrl;
    } catch (error) {
      console.error('Error generating image:', error);
      // Return a fallback placeholder if image generation fails
      return `https://via.placeholder.com/1024x1024/8B5CF6/FFFFFF?text=${encodeURIComponent(imagePrompt)}`;
    }
  },

  // Enhance image prompt based on illustration style
  enhanceImagePrompt(basePrompt: string, style: string): string {
    const styleEnhancements: { [key: string]: string } = {
      watercolor: "Beautiful watercolor painting style, soft colors, artistic brushstrokes, children's book illustration",
      cartoon: "Cute cartoon style, bright colors, simple shapes, friendly characters, children's book illustration",
      realistic: "Realistic illustration style, detailed, natural colors, high quality, children's book illustration",
      minimalist: "Minimalist illustration style, clean lines, simple shapes, limited color palette, children's book illustration"
    };

    const enhancement = styleEnhancements[style] || styleEnhancements.watercolor;
    
    return `${basePrompt}. ${enhancement}. Safe for children, no scary elements, warm and inviting atmosphere.`;
  },

  // Helper functions
  getPageCount(storyLength: string): number {
    switch (storyLength) {
      case 'short': return 3;
      case 'medium': return 5;
      case 'long': return 8;
      default: return 5;
    }
  },

  calculateReadTime(pages: StoryPage[]): number {
    // Estimate 30 seconds per page for young children
    return Math.ceil(pages.length * 0.5);
  },

  // New method to process and upload images after story generation
  async processAndUploadImages(pages: StoryPage[], storyId: string): Promise<StoryPage[]> {
    const processedPages = [];
    
    for (const page of pages) {
      if (page.imageUrl && page.imageUrl.startsWith('https://oaidalleapiprodscus.blob.core.windows.net')) {
        try {
          // Download from DALL-E and upload to Supabase Storage
          const uploadedImage = await storageService.downloadAndUploadImage(
            page.imageUrl,
            storyId,
            page.pageNumber
          );
          
          // Update the page with the new Supabase Storage URL
          processedPages.push({
            ...page,
            imageUrl: uploadedImage.url
          });
        } catch (error) {
          console.error(`Failed to process image for page ${page.pageNumber}:`, error);
          // Keep the original URL if upload fails
          processedPages.push(page);
        }
      } else {
        // Keep non-DALL-E URLs as they are
        processedPages.push(page);
      }
    }
    
    return processedPages;
  }
}; 